"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiNotificationsResponse } from "@/generated/models/ApiNotificationsResponse";
import type { ConnectedWalletAccount } from "@/services/auth/auth.utils";
import { commonApiFetch } from "@/services/api/common-api";
import useCapacitor from "./useCapacitor";
type ConnectedAccountUnreadCounts = Readonly<Record<string, number>>;
const POLL_INTERVAL_MS = 15000;
const toAddressKey = (address: string): string => address.toLowerCase();
const clampUnreadCount = (count: number | null | undefined): number => {
  if (typeof count !== "number" || Number.isNaN(count) || count <= 0) {
    return 0;
  }
  return Math.floor(count);
};
const fetchUnreadCountForAccount = async (account: ConnectedWalletAccount): Promise<number> => {
  if (!account.jwt) {
    return 0;
  }
  const notifications = await commonApiFetch<ApiNotificationsResponse>({
    endpoint: "notifications",
    params: {
      limit: "1"
    },
    headers: {
      Authorization: `Bearer ${account.jwt}`
    }
  });
  return clampUnreadCount(notifications.unread_count);
};
export function useConnectedAccountsUnreadNotifications(accounts) {
  const $ = _c(16);
  const {
    isCapacitor
  } = useCapacitor();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== accounts) {
    t0 = accounts.map(_temp);
    $[0] = accounts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = [QueryKey.CONNECTED_ACCOUNT_UNREAD_NOTIFICATIONS, "connected-account-unread-counts", t0];
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const queryKey = t1 as const;
  let t2;
  if ($[4] !== accounts || $[5] !== queryClient || $[6] !== queryKey) {
    t2 = async () => {
      if (accounts.length === 0) {
        return {};
      }
      const previousCounts = queryClient.getQueryData(queryKey) ?? {};
      const results = await Promise.allSettled(accounts.map(_temp2));
      const nextCounts = {};
      accounts.forEach((account_1, index) => {
        const addressKey = toAddressKey(account_1.address);
        const result = results[index];
        if (!result) {
          const previousCount = previousCounts[addressKey];
          if (typeof previousCount === "number") {
            nextCounts[addressKey] = previousCount;
          }
          return;
        }
        if (result.status === "fulfilled") {
          nextCounts[addressKey] = result.value;
          return;
        }
        const previousCount_0 = previousCounts[addressKey];
        if (typeof previousCount_0 === "number") {
          nextCounts[addressKey] = previousCount_0;
        }
      });
      return nextCounts;
    };
    $[4] = accounts;
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = accounts.length > 0;
  const t4 = !isCapacitor;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getDefaultQueryRetry();
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== queryKey || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t6 = {
      queryKey,
      queryFn: t2,
      enabled: t3,
      refetchInterval: POLL_INTERVAL_MS,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t4,
      ...t5
    };
    $[9] = queryKey;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data
  } = useQuery(t6);
  let t7;
  if ($[14] !== data) {
    t7 = data ?? {};
    $[14] = data;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function _temp2(account_0) {
  return fetchUnreadCountForAccount(account_0);
}
function _temp(account) {
  return toAddressKey(account.address);
}